import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  MessageSquare,
  Link2,
  FolderOpen,
  Wallet,
  Briefcase,
  Users,
  ShieldCheck,
  Megaphone,
  Smartphone,
  Mail,
} from 'lucide-react';
import { ProfessionalDashboardSchil, WERKPLEK_SIGNALEN_KEY } from '../components/ProfessionalDashboardSchil';
import { WerkplekSignaalLijst } from '../components/WerkplekSignaalLijst';
import { werkplekApi, VENSTERS, type SignalenResponse, type Venster } from '../services/professionalWerkplekApi';

const VENSTER_ICOON: Record<Venster, React.ComponentType<{ className?: string }>> = {
  new_message: MessageSquare,
  connection_request: Link2,
  dossier_update: FolderOpen,
  invoice: Wallet,
  vacancy: Briefcase,
  community: Users,
  compliance: ShieldCheck,
  platform: Megaphone,
};

const ZICHTBAAR_PER_VENSTER = 3;

/** Schakelaar in de stijl [ SMS ] | [ E-mail ]: aan = navy, uit = grijs. */
const KanaalKnop: React.FC<{
  aan: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  bezig: boolean;
  onClick: () => void;
}> = ({ aan, label, icon: Icon, bezig, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={aan}
    disabled={bezig}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold border transition-colors disabled:opacity-60 ${
      aan ? 'bg-navy-900 border-navy-900 text-white' : 'bg-white border-gray-300 text-navy-400 hover:border-navy-300'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

/**
 * Dashboard › Alle signalen: Admin-stijl vensters per signaaltype. Elk venster
 * heeft twee onafhankelijke grendels (SMS en e-mail); in-app blijft altijd aan.
 */
const ProfessionalWerkplekSignalenPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();
  const [uitgeklapt, setUitgeklapt] = useState<Venster | null>(null);
  const { data, isLoading, isError } = useQuery({ queryKey: WERKPLEK_SIGNALEN_KEY, queryFn: werkplekApi.signalen });

  const schakel = useMutation({
    mutationFn: (v: { venster: Venster; kanaal: 'sms' | 'email'; aan: boolean }) =>
      werkplekApi.zetVenster(v.venster, { [v.kanaal]: v.aan }),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: WERKPLEK_SIGNALEN_KEY });
      const vorige = queryClient.getQueryData<SignalenResponse>(WERKPLEK_SIGNALEN_KEY);
      queryClient.setQueryData<SignalenResponse>(WERKPLEK_SIGNALEN_KEY, (oud) =>
        oud ? { ...oud, voorkeuren: { ...oud.voorkeuren, [`${v.kanaal}_${v.venster}`]: v.aan } } : oud,
      );
      return { vorige };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.vorige) queryClient.setQueryData(WERKPLEK_SIGNALEN_KEY, ctx.vorige);
      toast.error(t('werkplek.signalenPagina.opslaanFout'));
    },
    onSuccess: (voorkeuren, v) => {
      queryClient.setQueryData<SignalenResponse>(WERKPLEK_SIGNALEN_KEY, (oud) => (oud ? { ...oud, voorkeuren } : oud));
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success(
        t(v.aan ? 'werkplek.signalenPagina.kanaalAan' : 'werkplek.signalenPagina.kanaalUit', {
          kanaal: v.kanaal === 'sms' ? 'SMS' : t('werkplek.signalenPagina.email'),
          venster: t(`werkplek.venster.${v.venster}.titel`),
        }),
      );
    },
  });

  return (
    <ProfessionalDashboardSchil titel={t('werkplek.signalenPagina.titel')} ondertitel={t('werkplek.signalenPagina.ondertitel')}>
      <div className="p-4 sm:p-6">
        {isError && <p className="text-sm text-red-600">{t('werkplek.laadFout')}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {VENSTERS.map((venster) => {
            const Icon = VENSTER_ICOON[venster];
            const signalen = (data?.signalen ?? []).filter((s) => s.venster === venster);
            const teller = data?.tellers?.[venster];
            const sms = !!data?.voorkeuren?.[`sms_${venster}`];
            const email = !!data?.voorkeuren?.[`email_${venster}`];
            const toonAlle = uitgeklapt === venster;
            const zichtbaar = toonAlle ? signalen : signalen.slice(0, ZICHTBAAR_PER_VENSTER);
            const bezig = schakel.isPending && schakel.variables?.venster === venster;
            return (
              <section key={venster} className="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col">
                <header className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
                  <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-navy-900" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
                      {t(`werkplek.venster.${venster}.titel`)}
                      {(teller?.ongelezen ?? 0) > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gold-400 text-navy-900 text-xs font-bold">
                          {teller!.ongelezen}
                        </span>
                      )}
                    </h2>
                    <p className="text-xs text-navy-500 mt-0.5">{t(`werkplek.venster.${venster}.omschrijving`)}</p>
                  </div>
                </header>

                <div className="flex items-center justify-between gap-2 px-4 py-2 bg-navy-50/50 border-b border-gray-100">
                  <span className="text-xs font-medium text-navy-500">{t('werkplek.signalenPagina.meldMij')}</span>
                  <div className="flex items-center gap-1.5">
                    <KanaalKnop
                      aan={sms}
                      label="SMS"
                      icon={Smartphone}
                      bezig={bezig}
                      onClick={() => schakel.mutate({ venster, kanaal: 'sms', aan: !sms })}
                    />
                    <KanaalKnop
                      aan={email}
                      label={t('werkplek.signalenPagina.email')}
                      icon={Mail}
                      bezig={bezig}
                      onClick={() => schakel.mutate({ venster, kanaal: 'email', aan: !email })}
                    />
                  </div>
                </div>

                <div className="p-3 flex-1">
                  {isLoading ? (
                    <div className="h-12 rounded-lg bg-gray-50 animate-pulse" />
                  ) : signalen.length === 0 ? (
                    <p className="text-xs text-navy-400 px-1 py-3">{t('werkplek.signalenPagina.geenSignalen')}</p>
                  ) : (
                    <>
                      <WerkplekSignaalLijst signalen={zichtbaar} compact />
                      {signalen.length > ZICHTBAAR_PER_VENSTER && (
                        <button
                          type="button"
                          onClick={() => setUitgeklapt(toonAlle ? null : venster)}
                          className="mt-2 px-1 text-xs font-semibold text-navy-700 hover:text-navy-900"
                        >
                          {toonAlle
                            ? t('werkplek.signalenPagina.minder')
                            : t('werkplek.signalenPagina.toonAlle', { count: signalen.length })}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </ProfessionalDashboardSchil>
  );
};

export default ProfessionalWerkplekSignalenPage;
